import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from "@angular/core";
import { Fiat, FiatWallet, Offer } from "@models";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { StockTestService } from "@main-services/stock-test.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { convertTradableToFiat } from "@helpers/tradable-convert";
import { FiatTransaction } from "@models/transaction";
import { AccountService } from "../../../account/services/account.service";
import { TooltipCoinOptions, TooltipService, TooltipTradableAction } from "@main-services/tooltip.service";
import { FiatService } from "@main-services/currency";
import { UserService } from "@main-services/user.service";
import { FiatWithdrawalModalComponent } from "../../../main/components/step-summary/fiat-transactions/fiat-withdraw-modal/fiat-withdraw-modal.component";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { Index } from "@models/asset/index/index.model";
import { WalletBalance } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance.model";
import { TRADE_ACTION } from "@modules/action-buttons/action-buttons.config";
import { ActionButton } from "@modules/action-buttons/entities/action-button.model";
import { ACTION_BUTTONS } from "@modules/action-buttons/entities/action-button.enum";
import {
	ButtonStatus,
	SendEntryPointService
} from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { FiatDepositEntryService } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit-entry.service";
import { map, take } from "rxjs/operators";
import { combineLatest, EMPTY, Observable, of } from "rxjs";
import { WalletsBalancesFacadeService } from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.facade.service";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";
import { CheckoutNavigationService } from "@modules/checkout/services/checkout-navigation.service";
import { CheckoutRouteName } from "@modules/checkout/entities/checkout.model";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { TinShieldEntryHelper } from "@modules/taxes/tin/services/tin-shield-entry.helper";

@Component({
	selector: "fiat-action-buttons",
	templateUrl: "./fiat-action-buttons.component.html",
	styleUrls: ["./fiat-action-buttons.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class FiatActionButtonsComponent implements OnInit {
	@Input() public wallet: FiatWallet = null;
	public balance$: Observable<WalletBalance> = EMPTY;

	@Input() public alternateFiat: Fiat = null;
	public actionButtons: ActionButton[] = [];
	private coinMinBuy: number = 0;
	private disabledMap: Map<string, Observable<boolean>> = null;
	private sendStatus$: Observable<ButtonStatus> = EMPTY;
	private sendTooltip$: Observable<string> = EMPTY;
	private sendDisabledStatus$: Observable<boolean> = EMPTY;
	private withdrawTooltip$: Observable<string> = EMPTY;
	private withdrawDisabledStatus$: Observable<boolean> = EMPTY;

	public checkoutFlowEnabled$ = this.launchDarklyService.isActive$(LD_FLAG.CHECKOUT_FLOW_ENABLED);
	private readonly tinShieldEntryHelper = inject(TinShieldEntryHelper);

	public constructor(
		protected modalService: ModalService,
		protected accountService: AccountService,
		protected fiatService: FiatService,
		protected tradableWalletListService: TradableWalletListService,
		protected userService: UserService,
		protected tradableListService: TradableListService,
		protected masterdataService: MasterdataService,
		protected tooltipService: TooltipService,
		protected stockTestService: StockTestService,
		protected stockTestHelperService: StockTestHelperService,
		protected fiatDepositEntryService: FiatDepositEntryService,
		protected sendEntryPoint: SendEntryPointService,
		protected walletBalancesFacade: WalletsBalancesFacadeService,
		protected userFiatFundsService: UserFiatFundsService,
		protected checkoutNavigationService: CheckoutNavigationService,
		protected launchDarklyService: LaunchDarklyService
	) {
		this.coinMinBuy = Number.MAX_VALUE;
		this.tradableListService.list.forEach((c) => {
			if (this.coinMinBuy > c.getMinBuy()) {
				this.coinMinBuy = c.getMinBuy();
			}
		});
	}

	public get fiat(): Fiat {
		return this.alternateFiat || this.accountService.getDefaultFiat() || new Fiat();
	}

	public ngOnInit(): void {
		this.sendStatus$ = this.sendEntryPoint.getStatusForFiatWallet$(this.wallet.getId());
		this.sendTooltip$ = this.sendStatus$.pipe(map((status: ButtonStatus) => status.message));
		this.sendDisabledStatus$ = this.sendStatus$.pipe(map((status: ButtonStatus) => status.isDisabled));
		this.balance$ = this.walletBalancesFacade.getFiatWalletBalance$(this.wallet.getId());
		this.withdrawTooltip$ = this.getWithdrawTooltip();
		this.withdrawDisabledStatus$ = this.isWithdrawDisabled();

		this.initDisabledMap();
		this.actionButtons = [
			{
				name: ACTION_BUTTONS.BUY,
				labelKey: "button_buy",
				tooltipKey$: of(this.getBuyTooltip()),
				action: (checkoutFlowEnabled: boolean) => this.triggerTrade(ACTION_BUTTONS.BUY, checkoutFlowEnabled),
				disabled$: of(false),
				icon: "bpi__icon-buy"
			},
			{
				name: ACTION_BUTTONS.SELL,
				labelKey: "button_sell",
				tooltipKey$: of(this.getSellTooltip()),
				action: () => this.triggerTrade(ACTION_BUTTONS.SELL),
				disabled$: this.isActionButtonDisabled$(ACTION_BUTTONS.SELL),
				icon: "bpi__icon-sell"
			},
			{
				name: ACTION_BUTTONS.SEND,
				labelKey: "button_send_fiat",
				tooltipKey$: this.sendTooltip$,
				action: () => this.triggerSend(),
				disabled$: this.isActionButtonDisabled$(ACTION_BUTTONS.SEND),
				icon: "bpi__icon-send"
			},
			{
				name: ACTION_BUTTONS.DEPOSIT,
				labelKey: "button_deposit",
				tooltipKey$: of(this.getDepositTooltip()),
				action: () => this.triggerDeposit(),
				disabled$: this.isActionButtonDisabled$(ACTION_BUTTONS.DEPOSIT),
				icon: "bpi__icon-incoming"
			},
			{
				name: ACTION_BUTTONS.WITHDRAW,
				labelKey: "button_withdraw",
				tooltipKey$: this.withdrawTooltip$,
				action: () => this.triggerWithdraw(),
				disabled$: this.isActionButtonDisabled$(ACTION_BUTTONS.WITHDRAW),
				icon: "bpi__icon-outgoing"
			}
		];
	}

	public isActionButtonDisabled$(button: string): Observable<boolean> {
		if (this.isVerificationMandatory() || !this.wallet) {
			return of(true);
		}

		return this.disabledMap.get(button);
	}

	public getFiat(): Fiat {
		return this.fiat || this.fiatService.getItemById(this.wallet.getFiatId());
	}

	public minWithdrawFiatAmount(): string {
		return this.fiat.getMinWalletWithdraw();
	}

	private getPaymentsFlowTx(f: FiatWallet): FiatTransaction {
		const fiatTx = new FiatTransaction();
		fiatTx["preselected"] = f;
		fiatTx.setType("withdrawal");
		fiatTx.setFiatWalletId(f.getId());
		fiatTx.setDirectFiatTransaction(true);
		return fiatTx;
	}

	protected isVerificationMandatory(): boolean {
		return this.userService.isVerificationMandatory;
	}

	protected getTooltipString(options: TooltipCoinOptions): string {
		return this.tooltipService.getTooltip(options);
	}

	private initDisabledMap(): void {
		this.disabledMap = new Map<ACTION_BUTTONS, Observable<boolean>>([
			[ACTION_BUTTONS.BUY, of(this.isBuyDisabled())],
			[ACTION_BUTTONS.SELL, of(this.isSellDisabled())],
			[ACTION_BUTTONS.SEND, this.sendDisabledStatus$],
			[ACTION_BUTTONS.DEPOSIT, of(false)],
			[ACTION_BUTTONS.WITHDRAW, this.withdrawDisabledStatus$]
		]);
	}

	private getDefaultTooltip(action: TooltipTradableAction): string {
		const tooltipOptions: TooltipCoinOptions = {
			action,
			fiat: this.getFiat()
		};

		return this.getTooltipString(tooltipOptions);
	}

	private getDepositTooltip(): string {
		return this.getDefaultTooltip(TooltipTradableAction.DEPOSIT);
	}

	private getWithdrawTooltip(): Observable<string> {
		const minWithdrawal: number = +this.minWithdrawFiatAmount();

		return combineLatest([
			this.balance$,
			this.sendEntryPoint.checkIfCashPlusAssetAboveMinimum$(minWithdrawal, this.getFiat().getPid())
		]).pipe(
			map(([walletBalance, isCashPlusAssetAboveMinimum]) => {
				if (this.userService.user.isUnverified()) {
					return this.getDefaultTooltip(TooltipTradableAction.WITHDRAWAL);
				}

				// if the parked asset (cashplus) is available and is above the min treshold
				if (isCashPlusAssetAboveMinimum && minWithdrawal > walletBalance.balance) {
					return "cashplus_fiat_withdraw_balance_tooltip_description";
				}

				// if the parked asset (cashplus) is NOT available and current balance is below min treshold
				if (!isCashPlusAssetAboveMinimum && minWithdrawal > walletBalance.balance) {
					return "action_fiat_withdraw_below_min_tooltip";
				}

				if (walletBalance.operationalBalance < minWithdrawal) {
					return "recent_deposit_disclaimer";
				}
				return "";
			})
		);
	}

	private getSellTooltip(): string {
		if (this.userService.user.isUnverified()) {
			return this.getDefaultTooltip(TooltipTradableAction.SELL);
		}

		if (this.assetWalletsAboveMinSell().length <= 0) {
			return "no_wallet_with_enough_funds_to_sell";
		}
	}

	private getBuyTooltip(): string {
		if (this.userService.user.isUnverified()) {
			return this.getDefaultTooltip(TooltipTradableAction.BUY);
		}
	}

	private createFiatTransaction(): FiatTransaction {
		const fiatTransaction = new FiatTransaction();
		fiatTransaction.setFiatWalletId(this.wallet.getId());
		fiatTransaction.setDirectFiatTransaction(true);

		return fiatTransaction;
	}

	private triggerSend(): void {
		const fiatTx = this.getPaymentsFlowTx(this.wallet);
		this.sendEntryPoint.openFiatSendModal(fiatTx);
	}

	private triggerDeposit(): void {
		const fiatPid = this.fiatService.findById(this.wallet.getFiatId()).getPid() ?? "";
		this.fiatDepositEntryService.openDepositForWallet(this.wallet.getId(), fiatPid);
	}

	private triggerWithdraw(): void {
		this.tinShieldEntryHelper
			.handleTinStatusCheck$()
			.pipe(take(1))
			.subscribe({
				next: (isTinFrozen) => {
					if (isTinFrozen) {
						return;
					}
					const fiatTransaction = this.createFiatTransaction();
					fiatTransaction["preselected"] = this.wallet;
					fiatTransaction.setType("withdrawal");
					this.modalService.showModal(FiatWithdrawalModalComponent, { transaction: fiatTransaction });
				}
			});
	}

	private triggerTrade(action: string, checkoutFlowEnabled: boolean = false): void {
		if (action === ACTION_BUTTONS.BUY && !this.userFiatFundsService.hasFiatWalletsWithFunds()) {
			this.fiatDepositEntryService.openDepositNow();
		} else {
			if (checkoutFlowEnabled) {
				this.checkoutNavigationService.gotoByNavigation(CheckoutRouteName.AssetSelect);
				return;
			}
			const tradeAction = TRADE_ACTION[action];
			const offer = new Offer();
			offer.setFiatWalletId(this.wallet.getId());
			offer.setFiatId(this.wallet.getFiatId());
			offer.setType(tradeAction.type);

			const showModal: () => void = () =>
				this.modalService.create(tradeAction.component, { data: offer }).present();

			this.stockTestService.needsTest(offer.getCurrencyId())
				? this.stockTestHelperService.startTest(showModal)
				: showModal();
		}
	}

	private isWithdrawDisabled(): Observable<boolean> {
		const minWithdrawal: number = +this.minWithdrawFiatAmount();

		return this.balance$.pipe(
			map(
				({ operationalBalance }: WalletBalance) =>
					this.userService.user.isUnverified() || operationalBalance < minWithdrawal
			)
		);
	}

	private isBuyDisabled(): boolean {
		return !!this.wallet;
	}

	private isSellDisabled(): boolean {
		return this.assetWalletsAboveMinSell().length <= 0;
	}

	private assetWalletsAboveMinSell(): TradableWallet[] {
		return this.tradableWalletListService.getNonEmptyWallets().filter((wallet) => {
			const asset = this.tradableListService.getItemById(wallet.getCurrencyId());
			if (!asset || !asset.isSellActive()) {
				return false;
			}
			const balance =
				asset instanceof Index
					? wallet.getBalance()
					: convertTradableToFiat(asset, wallet.getBalance(), this.accountService.getDefaultFiat());
			return balance >= asset.getMinSell();
		});
	}
}
